import { useEffect, useMemo, useRef } from "react";
import { useUnreadSummary } from "./useUnreadSummary";
import { getAuthToken } from "../../util/auth";

export function useNotificationsSocket({ profileId, wsBaseUrl }) {
    const { refreshUnreadSummary } = useUnreadSummary();
    const wsRef = useRef(null);

    // Keep latest callback/value without re-subscribing
    const refreshRef = useRef(refreshUnreadSummary);
    refreshRef.current = refreshUnreadSummary;

    const profileRef = useRef(profileId);
    profileRef.current = profileId;

    // Reconnect backoff state
    const retryRef = useRef({ timer: null, attempt: 0 });

    const authToken = getAuthToken(); // assumed { token: "..." } or null
    const token = authToken?.token ? String(authToken.token) : "";

    const wsUrl = useMemo(() => {
        if (!profileId) return null;

        const base = `${wsBaseUrl}${profileId}/`;
        const urlWithToken = token ? `${base}?token=${encodeURIComponent(token)}` : base;

        // Optional: upgrade ws:// -> wss:// on https pages
        try {
            const u = new URL(urlWithToken);
            const isHttpsPage =
                typeof window !== "undefined" && window.location?.protocol === "https:";
            if (isHttpsPage && u.protocol === "ws:") u.protocol = "wss:";
            return u.toString();
        } catch {
            return urlWithToken;
        }
    }, [profileId, wsBaseUrl, token]);

    useEffect(() => {
        if (!wsUrl) return;

        // Snapshot retry state object for lint + stable cleanup reference
        const retryState = retryRef.current;

        // Close previous socket if any
        if (wsRef.current) {
            try {
                wsRef.current.onopen = null;
                wsRef.current.onmessage = null;
                wsRef.current.onclose = null;
                wsRef.current.onerror = null;
                wsRef.current.close();
            } catch {}
            wsRef.current = null;
        }

        // Clear pending retries (using snapshot)
        if (retryState.timer) {
            clearTimeout(retryState.timer);
            retryState.timer = null;
        }
        retryState.attempt = 0;

        let closedByCleanup = false;

        const connect = () => {
            if (closedByCleanup) return;

            const ws = new WebSocket(wsUrl);
            wsRef.current = ws;

            ws.onopen = async () => {
                retryState.attempt = 0;

                const pid = profileRef.current;
                if (!pid) return;

                try {
                    await refreshRef.current(pid);
                } catch {
                    // ignore
                }
            };

            ws.onmessage = async (event) => {
                try {
                    const payload = JSON.parse(event.data);
                    if (payload?.type === "notification") {
                        const pid = profileRef.current;
                        if (!pid) return;
                        await refreshRef.current(pid);
                    }
                } catch {
                    // ignore parse errors
                }
            };

            ws.onclose = () => {
                if (closedByCleanup) return;

                const attempt = Math.min(6, (retryState.attempt || 0) + 1);
                retryState.attempt = attempt;

                // 500ms, 1s, 2s, 4s, 8s, 10s...
                const delay = Math.min(10000, 500 * Math.pow(2, attempt - 1));

                retryState.timer = setTimeout(() => {
                    retryState.timer = null;
                    connect();
                }, delay);
            };

            ws.onerror = () => {
                // onclose handles reconnect
            };
        };

        connect();

        return () => {
            closedByCleanup = true;

            // Cleanup retry timer (using snapshot)
            if (retryState.timer) {
                clearTimeout(retryState.timer);
                retryState.timer = null;
            }

            // Cleanup socket
            if (wsRef.current) {
                try {
                    wsRef.current.close();
                } catch {}
                wsRef.current = null;
            }
        };
    }, [wsUrl]);
}
